import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Check, Globe, Menu, X } from 'lucide-react'
import { CALENDLY_URL } from './constants'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useLanguage } from '@/i18n/LanguageContext'
import type { Lang } from '@/i18n/translations'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const LANGUAGES: { code: Lang; label: string }[] = [
  { code: 'it', label: 'Italiano' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
]

export default function Header() {
  const { lang, setLang, t } = useLanguage()
  const location = useLocation()
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)

  const NAV_LINKS = [
    { label: t.header.nav.about, href: '#chi-sono' },
    { label: t.header.nav.services, href: '#servizi' },
    { label: t.header.nav.how, href: '#come-funziona' },
    { label: t.header.nav.itineraries, href: '/itinerari' },
    { label: t.collaborations.navLabel, href: '/collaborazioni' },
    { label: t.header.nav.reviews, href: '#recensioni' },
  ]

  const handleNavClick = (event: React.MouseEvent, href: string) => {
    event.preventDefault()
    setOpen(false)
    if (href.startsWith('#')) {
      if (location.pathname === '/') {
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else {
        navigate(`/${href}`)
      }
    } else {
      navigate(href)
      window.scrollTo({ top: 0 })
    }
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-cream/90 backdrop-blur-md shadow-[0_1px_0_rgba(15,43,70,0.08),0_8px_24px_rgba(15,43,70,0.06)]' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center px-4 py-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="group flex shrink-0 items-center"
            aria-label={t.header.backToTop}
            onClick={(event) => {
              if (location.pathname === '/') {
                event.preventDefault()
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }
            }}
          >
            <img
              src="/images/LOGO-orizz.png"
              alt={t.header.logoAlt}
              className="h-20 w-auto sm:h-24"
            />
          </Link>

          <nav className="ml-10 hidden flex-1 items-center justify-evenly gap-10 lg:flex" aria-label={t.header.mainNavAria}>
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(event) => handleNavClick(event, link.href)}
                className="text-sm font-medium tracking-wide text-ink/80 transition-colors hover:text-terracotta"
              >
                {link.label}
              </a>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-1.5 rounded-full border border-ink/15 bg-[#F5EADF] px-3.5 py-2 text-sm font-medium tracking-wide text-ink/80 outline-none transition-colors hover:border-terracotta hover:text-terracotta"
                aria-label={t.header.languageLabel}
              >
                <Globe className="h-4 w-4" />
                {lang.toUpperCase()}
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="rounded-2xl border-ink/10 bg-cream p-1.5 text-ink shadow-xl shadow-ink/10"
              >
                {LANGUAGES.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    onSelect={() => setLang(l.code)}
                    className="flex cursor-pointer items-center justify-between gap-4 rounded-xl px-3 py-2 text-sm text-ink/80 focus:bg-terracotta/10 focus:text-terracotta"
                  >
                    {l.label}
                    {lang === l.code ? (
                      <Check className="h-4 w-4 text-terracotta" />
                    ) : (
                      <span className="w-4" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-cream shadow-md shadow-terracotta/25 transition-transform duration-200 hover:scale-105"
            >
              {t.common.ctaLabel}
            </a>
          </nav>

          <button
            type="button"
            onClick={() => setOpen(true)}
            className="ml-auto flex h-11 w-11 items-center justify-center rounded-full border-2 border-ink text-ink lg:hidden sm:translate-x-1 md:translate-x-2"
            aria-label={t.header.openMenu}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ink text-cream lg:hidden"
          >
            <div className="flex items-center justify-between px-4 py-4 sm:px-6">
              <span className="flex items-center">
                <img
                  src="/images/LOGO-orizz2.png"
                  alt={t.header.logoAltShort}
                  className="h-20 w-auto"
                />
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-cream"
                aria-label={t.header.closeMenu}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex flex-1 flex-col items-start justify-center gap-6 px-8" aria-label={t.header.mobileNavAria}>
              {NAV_LINKS.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={(event) => handleNavClick(event, link.href)}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.35 }}
                  className="font-display text-4xl italic text-cream transition-colors hover:text-terracotta"
                >
                  {link.label}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.35 }}
                className="mt-8 flex flex-wrap items-center gap-5"
              >
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => setLang(l.code)}
                    className={`text-sm font-semibold uppercase tracking-[0.2em] transition-colors ${
                      lang === l.code ? 'text-terracotta' : 'text-cream/50 hover:text-cream'
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.35 }}
                className="mt-6 flex flex-col items-center gap-3"
              >
                <a
                  href={CALENDLY_URL}
                  onClick={() => setOpen(false)}
                  className="whitespace-nowrap rounded-full bg-terracotta px-8 py-4 text-lg font-semibold text-cream shadow-lg shadow-terracotta/30"
                >
                  {t.common.ctaLabel}
                </a>
                <p className="text-center text-sm text-cream/60">
                  {t.common.freeNote}
                </p>
              </motion.div>
            </nav>

          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
